import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /contact — public surface only.
 *
 * Family law · `support_ladder_private_strategy_2026_08_13` (Founder plain-ink):
 * the internal mechanism (auto-canner → smart-auto → warden → founder) is
 * strategy, not disclosure. The public sees one surface: a contact form,
 * a promise, and a response-time. No ladder, no tier numbers, no escalation
 * mechanism. Backend machinery in support_ladder.py stays untouched.
 */

// Plain-language states shown to the visitor. Never expose the underlying
// tier number or the mechanism kind.
const STATE_LABEL = {
  1: "On file",
  2: "Reviewing",
  3: "Human reviewing",
  4: "Escalated",
};

// Every reply reads as if from Resilience — no auto-canner / smart-auto /
// warden / founder attribution. Only the visitor's own posts are marked "You".
function replyLabel(kind) {
  return kind === "user" ? "You" : "Resilience Studios";
}

function TicketView({ ticket, onEscalate, escalating }) {
  const canAskHuman = ticket.current_tier < 3;
  const humanOnIt = ticket.current_tier >= 3;
  return (
    <div className="space-y-8" data-testid="ticket-view">
      <div className="border border-[#27272a] bg-[#0b0b0d] p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00]">
              Ticket · {ticket.id}
            </p>
            <p className="mt-2 font-display text-2xl text-[#ededed]" data-testid="ticket-subject">
              {ticket.subject}
            </p>
            <p className="mt-1 font-body text-sm text-[#a1a1aa]">
              From <span className="text-[#ededed]">{ticket.name}</span> · {ticket.email}
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#52525b]">Status</p>
            <p
              className="font-mono text-lg text-[#ff5a00] mt-1"
              data-testid="ticket-status"
            >
              {STATE_LABEL[ticket.current_tier] || "On file"}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4" data-testid="ticket-exchanges">
        {ticket.exchanges.map((ex, i) => {
          const mine = ex.kind === "user";
          return (
            <div
              key={i}
              data-testid={`ticket-exchange-${i}`}
              className={`border p-5 ${
                mine
                  ? "border-[#27272a] bg-[#141417]"
                  : "border-[#ff5a00]/25 bg-[#0b0b0d]"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <p
                  className={`font-mono text-[10px] uppercase tracking-[0.32em] ${
                    mine ? "text-[#a1a1aa]" : "text-[#ff5a00]"
                  }`}
                >
                  {replyLabel(ex.kind)}
                </p>
                <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#52525b]">
                  {new Date(ex.at).toLocaleString()}
                </p>
              </div>
              <p className="font-body text-[#ededed] leading-relaxed whitespace-pre-wrap">
                {ex.content}
              </p>
            </div>
          );
        })}
      </div>

      <div className="border-t border-white/5 pt-8">
        {humanOnIt ? (
          <p className="font-body text-[#a1a1aa]" data-testid="ticket-human-on-it">
            A human at Resilience is reading your ticket. You'll get a direct reply
            by email — please keep an eye on your inbox.
          </p>
        ) : canAskHuman ? (
          <>
            <p className="font-body text-[#a1a1aa] mb-4">
              If the reply above did not resolve your ticket, ask a human at
              Resilience to look at it directly.
            </p>
            <button
              type="button"
              disabled={escalating}
              onClick={onEscalate}
              data-testid="ticket-ask-human-btn"
              className="px-6 py-3 border border-[#ff5a00] text-[#ff5a00] font-mono text-[11px] uppercase tracking-[0.32em] hover:bg-[#ff5a00] hover:text-[#050505] transition-colors disabled:opacity-50"
            >
              {escalating ? "Handing off…" : "Ask a human to look again"}
            </button>
          </>
        ) : (
          <p className="font-body text-[#a1a1aa]" data-testid="ticket-standing-reply">
            Reply is on file. A member of Resilience will follow up directly
            if the response above needs anything more.
          </p>
        )}
      </div>
    </div>
  );
}

function TicketForm({ onSubmitted }) {
  // URL prefill — /contact?subject=...&product_slug=...&message=...
  // Founder plain-ink 2026-08-22: The Founder retainer page routes here with
  // pre-filled inquiry copy so respectable philanthropic orgs reach us in two
  // clicks. Also works for any product page that wants to hand off a warm lead.
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(params.get("subject") || "");
  const [message, setMessage] = useState(params.get("message") || "");
  const [productSlug, setProductSlug] = useState(params.get("product_slug") || "");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = useMemo(
    () => name.trim() && email.trim() && subject.trim() && message.trim() && !submitting,
    [name, email, subject, message, submitting]
  );

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/support/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
          product_slug: productSlug.trim() || null,
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`ticket create failed: ${res.status} ${t}`);
      }
      const data = await res.json();
      onSubmitted(data.ticket);
      if (data.duplicate) {
        toast.info("We already had a matching ticket — showing that one.");
      } else {
        toast.success("Ticket on file. A reply is below.");
      }
    } catch (err) {
      toast.error(err.message || "Could not file ticket");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="ticket-form">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mb-2">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="ticket-input-name"
            className="w-full bg-[#0b0b0d] border border-[#27272a] px-4 py-3 text-[#ededed] font-body focus:outline-none focus:border-[#ff5a00]"
            required
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mb-2">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="ticket-input-email"
            className="w-full bg-[#0b0b0d] border border-[#27272a] px-4 py-3 text-[#ededed] font-body focus:outline-none focus:border-[#ff5a00]"
            required
          />
        </div>
      </div>
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mb-2">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid="ticket-input-subject"
          placeholder="What is this about? (refund, delivery, revision, quote…)"
          className="w-full bg-[#0b0b0d] border border-[#27272a] px-4 py-3 text-[#ededed] font-body focus:outline-none focus:border-[#ff5a00]"
          required
        />
      </div>
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mb-2">Message</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="ticket-input-message"
          rows={7}
          className="w-full bg-[#0b0b0d] border border-[#27272a] px-4 py-3 text-[#ededed] font-body focus:outline-none focus:border-[#ff5a00]"
          required
        />
      </div>
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mb-2">
          Related product (optional)
        </label>
        <input
          type="text"
          value={productSlug}
          onChange={(e) => setProductSlug(e.target.value)}
          data-testid="ticket-input-product"
          placeholder="e.g. animated-feature, screenplay, website"
          className="w-full bg-[#0b0b0d] border border-[#27272a] px-4 py-3 text-[#ededed] font-body focus:outline-none focus:border-[#ff5a00]"
        />
      </div>
      <button
        type="submit"
        disabled={!canSubmit}
        data-testid="ticket-submit-btn"
        className="px-8 py-4 bg-[#ff5a00] text-[#050505] font-mono text-[11px] uppercase tracking-[0.32em] hover:bg-[#ff8f00] transition-colors disabled:opacity-50"
      >
        {submitting ? "Filing…" : "Send"}
      </button>
    </form>
  );
}

export default function Contact() {
  const [ticket, setTicket] = useState(null);
  const [escalating, setEscalating] = useState(false);

  async function escalate() {
    if (!ticket) return;
    setEscalating(true);
    try {
      const res = await fetch(`${API}/api/support/tickets/${ticket.id}/escalate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`request failed: ${res.status} ${t}`);
      }
      const data = await res.json();
      setTicket(data.ticket);
      toast.success("A human at Resilience will look at your ticket.");
    } catch (err) {
      toast.error(err.message || "Could not hand off");
    } finally {
      setEscalating(false);
    }
  }

  return (
    <Layout>
      <section
        data-testid="page-contact"
        className="relative pt-32 pb-32 min-h-[80vh]"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] opacity-[0.07] blur-3xl"
            style={{ background: "radial-gradient(circle,#ff5a00,transparent 60%)" }}
          />
        </div>
        <div className="relative z-10 max-w-[900px] mx-auto px-6 sm:px-10 text-center">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00]"
          >
            Contact
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            data-testid="page-contact-headline"
            className="font-display uppercase leading-[0.92] mt-6 text-[#ededed]"
            style={{ fontSize: "clamp(2.75rem, 7vw, 5rem)", letterSpacing: "-0.005em" }}
          >
            Write to us.
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-10 font-body text-[#a1a1aa] leading-relaxed space-y-5"
          >
            <p data-testid="contact-promise">
              Every ticket lands somewhere and gets a real reply. Response inside
              twenty-four business hours.
            </p>
            <p data-testid="contact-body">
              Refunds, revisions, delivery, IP notices, or a quote outside the
              published tiers — send it below. If the first response does not
              resolve it, ask us to have a human at Resilience look again.
            </p>
          </motion.div>

          <div className="mt-16 text-left">
            {!ticket ? (
              <TicketForm onSubmitted={setTicket} />
            ) : (
              <TicketView ticket={ticket} onEscalate={escalate} escalating={escalating} />
            )}
          </div>

          {ticket && (
            <div className="mt-12">
              <button
                type="button"
                onClick={() => setTicket(null)}
                data-testid="ticket-new-btn"
                className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] hover:text-[#ff5a00] transition-colors"
              >
                ← Send another
              </button>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
